import axios from 'axios';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getLocation } from 'services/geolocation';
import {
  buildStripeCheckoutPresentmentBody,
  createOnboardingCheckoutSessionRequest,
} from 'services/subscriptions';
import {
  selectAllOnboardingPlans,
  selectCurrentUserSubscription,
} from 'store/selectors/subscriptions';
import { OnboardingCatalogItem, UserSubscription } from 'types';
import { formatSubscriptionDate } from 'utils/dates';
import { getMoneyLocale } from 'utils/money';

import { OnboardingSetupCard } from './OnboardingSetupCard';
import * as S from './styled';

type OnboardingSetupSectionProps = {
  catalogGeo: Awaited<ReturnType<typeof getLocation>> | null;
};

type OnboardingCardConfig = {
  slug: string;
  featured?: boolean;
  popularLabelKey?: string;
  titleKey: string;
  taglineKey: string;
  noteKey: string;
  bulletsKey: string;
  bulletsLabelKey: string;
  primaryCta?: boolean;
};

const ONBOARDING_SETUP_CARDS: OnboardingCardConfig[] = [
  {
    slug: 'kickstart',
    titleKey: 'changePlan.kickstartTitle',
    taglineKey: 'changePlan.kickstartTag',
    noteKey: 'changePlan.kickstartNote',
    bulletsKey: 'changePlan.kickstartBullets',
    bulletsLabelKey: 'changePlan.whatsIncluded',
  },
  {
    slug: 'full_service',
    featured: true,
    popularLabelKey: 'changePlan.setupPopular',
    titleKey: 'changePlan.fullServiceTitle',
    taglineKey: 'changePlan.fullServiceTag',
    noteKey: 'changePlan.fullServiceNote',
    bulletsKey: 'changePlan.fullServiceBullets',
    bulletsLabelKey: 'changePlan.whatsIncluded',
    primaryCta: true,
  },
  {
    slug: 'enterprise_onboarding',
    titleKey: 'changePlan.entOnboardTitle',
    taglineKey: 'changePlan.entOnboardTag',
    noteKey: 'changePlan.entOnboardNote',
    bulletsKey: 'changePlan.entOnboardBullets',
    bulletsLabelKey: 'changePlan.entOnboardPlus',
  },
];

const formatOnboardingOneTime = (
  row: OnboardingCatalogItem | undefined,
): string | null => {
  if (!row || row.oneTimeValue == null) return null;
  const code = (row.currency || 'CHF').toUpperCase();
  try {
    return new Intl.NumberFormat(getMoneyLocale(code), {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(row.oneTimeValue);
  } catch {
    return `${row.oneTimeValue} ${code}`;
  }
};

export const OnboardingSetupSection = ({
  catalogGeo,
}: OnboardingSetupSectionProps) => {
  const { t, i18n } = useTranslation();
  const subscription = useSelector(selectCurrentUserSubscription);
  const onboardingPlans = useSelector(selectAllOnboardingPlans);
  const [checkoutSlug, setCheckoutSlug] = useState<string | null>(null);

  const onboardingPriceBySlug = useMemo(() => {
    const prices = new Map<string, string | null>();
    [...onboardingPlans]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((row) => {
        prices.set(row.slug, formatOnboardingOneTime(row));
      });
    return prices;
  }, [onboardingPlans, i18n.language]);

  const getPurchasedOnboarding = useCallback(
    (slug: string) => {
      const row = onboardingPlans.find((plan) => plan.slug === slug);
      const list = (subscription as UserSubscription | null | undefined)
        ?.onboardingPurchases;
      if (!list?.length) return undefined;
      for (const purchase of list) {
        if (purchase.slug === slug) return purchase;
        if (row && purchase.onboardingPlanId === row.id) return purchase;
      }
      return undefined;
    },
    [onboardingPlans, subscription],
  );

  const startOnboardingCheckout = useCallback(
    async (slug: string) => {
      if (getPurchasedOnboarding(slug)) return;
      const row = onboardingPlans.find((plan) => plan.slug === slug);
      if (!row?.id || !row.stripePriceIdOneTime) {
        toast.error(t('changePlan.onboardingCheckoutUnavailable'));
        return;
      }
      setCheckoutSlug(slug);
      try {
        const { url } = await createOnboardingCheckoutSessionRequest({
          onboardingPlanId: row.id,
          ...buildStripeCheckoutPresentmentBody(catalogGeo),
        });
        window.location.href = url;
      } catch (err) {
        const status = axios.isAxiosError(err)
          ? err.response?.status
          : undefined;
        if (status === 409) {
          toast.error(t('changePlan.onboardingAlreadyPurchased'));
        } else {
          toast.error(t('changePlan.onboardingCheckoutFailed'));
        }
        setCheckoutSlug(null);
      }
    },
    [catalogGeo, getPurchasedOnboarding, onboardingPlans, t],
  );

  const renderPrice = (slug: string) => {
    const formatted = onboardingPriceBySlug.get(slug);
    if (formatted) {
      return (
        <>
          {formatted} <S.SetupPriceOl>{t('changePlan.oneTime')}</S.SetupPriceOl>
        </>
      );
    }
    return t('changePlan.priceUnavailable');
  };

  return (
    <S.SetupWrap>
      <S.SectionLabel>
        <S.SectionNum $accent>2</S.SectionNum>
        <S.SectionLabelText>
          <h2>{t('changePlan.onboardSectionTitle')}</h2>
          <p>{t('changePlan.onboardSectionSub')}</p>
        </S.SectionLabelText>
      </S.SectionLabel>
      <S.SetupGrid>
        {ONBOARDING_SETUP_CARDS.map((card) => {
          const purchased = getPurchasedOnboarding(card.slug);
          const purchasedDateLabel = purchased?.createdAt
            ? t('changePlan.onboardingPurchasedOn', {
                date: formatSubscriptionDate(
                  purchased.createdAt,
                  i18n.language,
                ),
              })
            : undefined;

          return (
            <OnboardingSetupCard
              key={card.slug}
              featured={card.featured}
              popularLabel={
                card.popularLabelKey ? t(card.popularLabelKey) : undefined
              }
              title={t(card.titleKey)}
              tagline={t(card.taglineKey)}
              price={renderPrice(card.slug)}
              note={t(card.noteKey)}
              bulletsLabel={t(card.bulletsLabelKey)}
              bullets={t(card.bulletsKey, { returnObjects: true }) as string[]}
              isPurchased={Boolean(purchased)}
              purchasedTitle={t('changePlan.onboardingPurchasedTitle')}
              purchasedDateLabel={purchasedDateLabel}
              ctaLabel={t('changePlan.bookOnboarding')}
              checkoutLoading={checkoutSlug === card.slug}
              primaryCta={card.primaryCta}
              onCheckout={() => startOnboardingCheckout(card.slug)}
            />
          );
        })}
      </S.SetupGrid>
    </S.SetupWrap>
  );
};
